import { DOCUMENT } from '@angular/common';
import type { OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ChangeDetectorRef, Component, inject, Input, QueryList, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import type { CraftXGritProductEntry } from '@core-mkt/interfaces/craft-xgrit-product-entry';
import { StateAbbrevDictionary } from '@core-mkt/interfaces/state-abbrev-dictionary.enum';
import { CouponAggregationService } from '@core-mkt/services/coupon-aggregation/coupon-aggregation.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { PricingService } from '@core-mkt/services/pricing/pricing.service';
import { ProductService } from '@core-mkt/services/product/product.service';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ProductPillService } from '@core-mkt/shared/components/product-pill/product-pill.service';
import { logError } from '@core-mkt/shared/lib/logger';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import { XGritApiService } from '@core-mkt/services/xgrit-api/xgrit-api.service';
import type { XGritCompleteProduct } from '@core-mkt/services/xgrit-api/xgrit-product';
import { SchemaService } from '@core-mkt/services/schema/schema.service';
import { CheckoutUrlBuilderService } from '@core-mkt/shared/services/checkout-url-builder/checkout-url-builder.service';
import { GlobalCouponOverrideService } from '@core-mkt/shared/services/global-coupon-override/global-coupon-override.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import type { WebinarScheduleResponse } from '../../webinar-schedule/webinar-schedule';
import { CardItemComponent } from './card-item/card-item.component';
import { NewCardItemComponent } from './new-card-item/new-card-item.component';
import type { ProductFilter, ProductItem } from './product-card';
import { ProductSection } from './product-card';
import { getProductCardSchema } from './product-card.schema';

/**
 * Shape of a single category element inside the dynamic
 * `craftData[categoryField]` array (e.g. `productCategory`,
 * `aceRealEstateProductCategory`, `insuranceProductCategory`,
 * `mortgageProductCategory`). The category field name is brand-configured at
 * runtime (`productCategoryHandle`), so the access is indexed by a dynamic
 * string and TypeScript can't resolve the concrete property type — this mirrors
 * the `CraftXGRitProductCategory` shape declared in craft-xgrit-product-entry.
 */
interface ProductCategory {
  aceCategoryCode: string;
  title: string;
}

/**
 * Angular wraps trusted HTML in a `SafeValue` object whose sanitized string is
 * stored on the internal `changingThisBreaksApplicationSecurity` field. After
 * `bypassSanitizer`, `card.textContent` can be either a raw string template or
 * one of these wrapper objects; we read the internal field to detect/inspect
 * already-sanitized HTML. This field is an internal Angular implementation
 * detail, not part of its public API.
 */
interface SafeHtmlInternal {
  changingThisBreaksApplicationSecurity?: string;
}

interface BannerWithOverwriteCoupon {
  overwriteCoupon?: string;
  promotionStartDateTime?: string;
  promotionEndsDateTime?: string;
  promotionReset?: Array<{ promotionEndsDateTime?: string }>;
  /**
   * Craft toggle for whether the banner is shown on the site. `true` = visible/active,
   * `false` = hidden. When the banner isn't visible to the user, its `overwriteCoupon`
   * must not silently replace product coupons at checkout.
   */
  showHidePromoBanner?: boolean;
}

@Component({
  selector: 'product-card',
  templateUrl: './product-card.component.html',
  styleUrls: ['./product-card.scss'],
  standalone: true,
  imports: [FormsModule, CardItemComponent, NewCardItemComponent],
})
export class ProductCardComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: ProductSection;
  @Input() horizontalAlignment: string;
  @ViewChildren(CardItemComponent) cardItems: QueryList<CardItemComponent>;
  hasContent = false;
  filterType = '';
  filterList: ProductFilter[] = [];
  products: ProductItem[] = [];
  unfilteredProducts: ProductItem[] = [];
  stateEnum = StateAbbrevDictionary;
  theme: ThemeInterface;
  categoryField = '';
  learnMore: boolean;
  hideSectionTitle = false;
  activeCoupon: string; // Used to store active coupon from the email cookie
  webinarDataCount = 0;
  private destroy$ = new Subject<void>();
  private readonly pillService = inject(ProductPillService);

  private redactor = inject(WysiwygRedactorService);
  private themeParser = inject(ThemeParserService);
  private ps = inject(ProductService);
  private pricing = inject(PricingService);
  private env = inject(EnvService);
  private xas = inject(XGritApiService);
  private cdr = inject(ChangeDetectorRef);
  private globalCouponOverrideService = inject(GlobalCouponOverrideService);
  private couponAggregationService = inject(CouponAggregationService);
  private schemaService = inject(SchemaService);
  private document = inject<Document>(DOCUMENT);
  private checkoutUrlBuilderService = inject(CheckoutUrlBuilderService);
  private route = inject(ActivatedRoute);

  constructor() {
    this.categoryField = this.env.get.brandConfig.productCategoryHandle;
    /**
     * subscribe to coupon code event to update pricing on the product
     *  cards if a coupon is applied
     */
    this.pricing.couponCodeEvent.pipe(takeUntil(this.destroy$)).subscribe((coupon: string) => {
      // Check if an email or phone capture coupon was just applied
      const couponCollection = this.couponAggregationService.collectAllCoupons();
      const captureCoupon = couponCollection?.coupons?.emailCapture || couponCollection?.coupons?.phoneCapture;

      if (captureCoupon?.trim()) {
        // Capture coupon found - trigger animated pricing for cards
        this.activeCoupon = null; // Let CouponAggregationService handle combining
        this.animateEmailCapturePrice();
      } else {
        // Standard coupon event handling
        this.activeCoupon = coupon;
        this.getProducts();
      }
    });
  }

  get ctaType(): string {
    return this.data.ctaStyle === null ? '' : this.data.ctaStyle;
  }

  get hideCta(): boolean {
    return this.data.hideCta;
  }

  get useCompactCards(): boolean {
    return this.data.useCompactCards;
  }

  get showCompactStrikethrough(): boolean {
    return !!this.data.showCompactStrikethrough;
  }

  get hasMultipleWebinarCards(): boolean {
    return this.webinarDataCount > 1;
  }

  get cardContainerWidth(): string {
    if (this.filterType.length > 0) {
      return 'lg:w-5/6';
    }
    return '';
  }

  get sectionHasPillRibbon(): boolean {
    return this.products.some(
      (product) => this.pillService.getVisiblePills(product.productEntry?.[0]?.productPills).length > 0,
    );
  }

  // 38px between card rows; 62px (38 + 24) when any card in the section carries a
  // visible pill, so the ribbon overhanging the card top doesn't crowd the row above.
  get cardGapClass(): string {
    return this.sectionHasPillRibbon ? 'gap-y-[62px]' : 'gap-y-[38px]';
  }

  get cardWidth(): string {
    switch (true) {
      case this.products.length < 2:
        return '';
      case this.products.length < 3:
        return 'w-full sm:w-1/2';
      default:
        return 'w-full sm:w-1/2 lg:w-1/3';
    }
  }

  ngOnInit(): void {
    this.setContent();
    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      backgroundColor: this.data.backgroundColor,
      backgroundImage: this.data.backgroundImage?.[0],
      textColor: this.data.textColor,
      columnCount: 0,
      columnDistribution: '',
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data && changes.data.currentValue !== changes.data.previousValue) {
      this.setContent();
    }
  }

  private setContent() {
    // `aarpProductCardList` is an AARP-only alias that Craft may send instead of
    // `productCardList`; it isn't on the ProductSection type, so read it through a
    // narrowed record rather than untyped index access.
    const aarpProductCardList = (this.data as unknown as Record<string, unknown>)['aarpProductCardList'];
    if (Array.isArray(aarpProductCardList)) {
      this.data.productCardList = aarpProductCardList as ProductItem[];
    }
    this.learnMore = this.data.productCardList.some((obj) => obj.learnMoreLink !== null);
    if (this.data.sectionTitle === null) {
      this.data.sectionTitle = '';
      this.hideSectionTitle = true;
    } else {
      this.hasContent = true;
      // setContent() runs from both ngOnChanges and ngOnInit. Only a raw Craft string needs
      // sanitizing; stringifying the SafeHtml from the first pass renders Angular's warning text.
      if (typeof this.data.sectionTitle === 'string') {
        this.data.sectionTitle = this.redactor.bypassSanitizer(this.data.sectionTitle);
      }
    }
    if (this.data.filterType && this.data.filterType !== null) {
      this.filterType = this.data.filterType;
    }

    // Get products immediately - CouponAggregationService reads banner coupon from route data
    this.getProducts();
  }

  /**
   * Handles email capture pricing updates for product cards
   * Product cards have complex nested pricing structure, so we use enhanced refresh
   * rather than direct animation like bundle/pricing-table components
   */
  private animateEmailCapturePrice(): void {
    // For product cards, we use enhanced product refresh to get updated pricing
    // with email capture coupons applied via CouponAggregationService
    this.getProducts();

    // Trigger change detection to ensure card items update with new pricing
    this.cdr.detectChanges();
  }

  addToFilterList(product: XGritCompleteProduct): void {
    if (product && this.filterType.length > 0) {
      const filterItem: ProductFilter = { value: '', label: '' };
      if (this.filterType === 'filterByState') {
        filterItem.value = product.craftData.productState;
        filterItem.label = this.stateEnum[product.craftData.productState as keyof typeof StateAbbrevDictionary];
      } else {
        const categories = this.getCategoryList(product.craftData);
        if (categories.length > 0) {
          filterItem.value = categories[0].aceCategoryCode;
          filterItem.label = categories[0].title;
          if (categories.length > 1) {
            for (let i = 1; i < categories.length; i++) {
              const category = categories[i];
              const filterItem: ProductFilter = { value: category.aceCategoryCode, label: category.title };
              this.pushFilter(filterItem);
            }
          }
        }
      }
      this.pushFilter(filterItem);
    }
  }

  pushFilter(filterItem: ProductFilter): void {
    if (this.filterList.findIndex((filter) => filter.label === filterItem.label) === -1) {
      this.filterList.push(filterItem);
      this.filterList.sort(function (a, b) {
        if (a.label < b.label) {
          return -1;
        }
        if (a.label > b.label) {
          return 1;
        }
        return 0;
      });
    }
  }

  doFiltering(value: string) {
    this.products = this.unfilteredProducts;
    if (value !== 'none' && this.filterType === 'filterByState') {
      this.products = this.products.filter(
        (product) => product.productEntry.length > 0 && product.productEntry[0].productState === value,
      );
    }
    if (value !== 'none' && this.filterType === 'filterByCategory') {
      this.products = this.products.filter((product) => {
        return this.getCategoryList(product.xGritProduct.craftData).find(
          (category) => category.aceCategoryCode === value,
        );
      });
    }
  }

  /**
   * Reads the brand-configured category array off craftData. The field name
   * (`this.categoryField`) is resolved from brand config at runtime, so the
   * access is dynamically keyed and untyped at compile time — narrow it to the
   * known `ProductCategory[]` element shape here rather than at every call site.
   */
  private getCategoryList(craftData: CraftXGritProductEntry | undefined): ProductCategory[] {
    const categories = (craftData as Record<string, unknown> | undefined)?.[this.categoryField];
    return Array.isArray(categories) ? (categories as ProductCategory[]) : [];
  }

  getFilterKey(filterType: string): string {
    const baseType = filterType.match(/PTDE|DE|AMI|FTA|DD|PL|COURSE|BUNDLE/);
    return baseType[0];
  }

  getProducts() {
    const craftProducts: CraftXGritProductEntry[] = [];

    if (!this.data) {
      return;
    }

    this.data.productCardList.forEach((product) => {
      if (this.ps.isProductValid(product.productEntry)) {
        // CouponAggregationService automatically collects all available coupons for the API
        craftProducts.push(this.ps.extractProduct(product.productEntry));
      }
    });

    if (craftProducts.length === 0) {
      this.parseProducts();
    } else {
      void this.ps.getMultiProductData(craftProducts).then((xgritProducts) => {
        this.parseProducts(xgritProducts);
      });
    }
  }

  parseProducts(xgritProducts?: XGritCompleteProduct[]) {
    this.products = [];
    this.unfilteredProducts = [];
    // Banner overwriteCoupon AND environment coupons (URL params, email cookie) are
    // page-scoped — same for every card on this page — so resolve them once per
    // parseProducts() run rather than re-walking the route tree and re-parsing
    // URL params for each card. Per-card craft coupons (from productEntry) are still
    // applied per call by CheckoutUrlBuilderService.
    const banner = this.collectBannerCouponsFromScopedRoute();
    // CouponAggregationService.collectAllCoupons() includes `globalBanner` from
    // route data without honoring the showHidePromoBanner / active-window gate
    // applied in collectBannerCouponsFromScopedRoute. Filter banner coupon strings
    // out of the environment-coupon set so the gate above is the SOLE authority on
    // whether a banner coupon contributes to the checkout URL.
    const environmentCoupons = this.couponAggregationService
      .getAllCouponCodes(this.couponAggregationService.collectAllCoupons())
      .filter((coupon) => !banner.all.includes(coupon));
    this.data?.productCardList?.forEach((card, index) => {
      if (card.cardTitle !== null) {
        card.cardTitle = this.redactor.bypassSanitizer(card.cardTitle);
        card.showTitle = true;
        this.hasContent = true;
      } else {
        card.cardTitle = '';
        card.showTitle = false;
      }
      card.productIdx = index;
      if (card.productEntry.length > 0) {
        const entry = this.ps.extractProduct(card.productEntry);

        // COUPON OVERHAUL FIX: Enhanced product matching for real-time pricing
        // When email capture/banner coupons are applied, the API response contains combined coupons
        // but the entry still has only the original craft coupon, so we need flexible matching

        // First try to match by both product ID and original craft coupon (exact match)
        let currentProduct = xgritProducts?.find((product) => {
          return (
            product.craftData.xgritProductId === entry.xgritProductId &&
            product.craftData.xgritCoupon === entry.xgritCoupon
          );
        });

        // If exact match fails, try product ID only - BUT only when entry has no explicit coupon
        // This handles email capture/banner coupons while preventing pricing mismatches
        if (!currentProduct && !entry.xgritCoupon) {
          currentProduct = xgritProducts?.find((product) => {
            return product.craftData.xgritProductId === entry.xgritProductId;
          });
        }

        // Final fallback to array index - only when entry has no explicit coupon
        // This prevents wrong pricing when coupons are explicitly specified
        if (!currentProduct && !entry.xgritCoupon && xgritProducts && xgritProducts[index]) {
          currentProduct = xgritProducts[index];
        }

        // Create new card object to trigger Angular change detection
        // This ensures the card-item components detect the pricing changes
        const updatedCard = { ...card };
        updatedCard.xGritProduct = currentProduct;
        updatedCard.showPricing = true;

        // Replace the card reference in the data array to trigger change detection
        this.data.productCardList[index] = updatedCard;
        card = updatedCard;

        // CRITICAL FIX: Store original textContent template before sanitization
        // This allows us to regenerate pricing HTML on subsequent updates
        const cardRecord = this.data.productCardList[index] as unknown as Record<string, unknown> | undefined;
        const originalTextContent =
          typeof cardRecord?.['_originalTextContent'] === 'string' ? cardRecord['_originalTextContent'] : undefined;

        if (card.textContent !== null && card.xGritProduct !== undefined) {
          // `textContent` is typed `any` on ProductItem; it can be a raw string
          // template or an Angular SafeValue wrapper. Narrow both shapes here.
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- the narrowed shape exposes the SafeValue field
          const textContent = card.textContent as string | SafeHtmlInternal;
          const sanitizedHtml =
            typeof textContent === 'object' && textContent !== null
              ? textContent.changingThisBreaksApplicationSecurity
              : undefined;

          // Check if textContent has pricing template variables
          const hasTemplateVars =
            typeof textContent === 'string' &&
            (textContent.includes('${this.crossedOutPrice}') || textContent.includes('${this.finalPrice}'));

          // Check if it's already sanitized HTML
          const isSanitizedHtml = sanitizedHtml !== undefined;

          if (hasTemplateVars || (isSanitizedHtml && originalTextContent)) {
            // Use original template if available, otherwise use current textContent
            const templateToUse = originalTextContent || textContent;

            // Store original template for future updates (only once)
            if (!originalTextContent && hasTemplateVars) {
              (this.data.productCardList[index] as unknown as Record<string, unknown>)['_originalTextContent'] =
                textContent;
            }

            // Generate fresh pricing HTML with updated product data
            card.textContent = this.pricing.getPricingText(card.xGritProduct, templateToUse as string);
            card.showPricing = false;
          } else if (isSanitizedHtml && sanitizedHtml.includes('$')) {
            card.showPricing = false;
          }
        }

        if (card.textContent !== null) {
          card.textContent = this.redactor.bypassSanitizer(card.textContent as string);
          card.showContent = true;
          this.hasContent = true;
        } else {
          card.textContent = '';
          card.showContent = false;
        }
        this.products.splice(card.productIdx, 0, card);
        this.unfilteredProducts.splice(card.productIdx, 0, card);
        this.addToFilterList(currentProduct);
        //force sort of filters, in case splice does not work as expected.
        this.products.sort((a, b) => {
          return a.productIdx - b.productIdx;
        });
        this.unfilteredProducts.sort((a, b) => {
          return a.productIdx - b.productIdx;
        });
      } else {
        if (card.textContent !== null) {
          if (/$[0-9]+/g.test(card.textContent as string)) {
            card.showPricing = false;
          }
          card.textContent = this.redactor.bypassSanitizer(card.textContent as string);
          card.showContent = true;
          this.hasContent = true;
        } else {
          card.textContent = '';
          card.showContent = false;
        }
        this.products.splice(card.productIdx, 0, card);
        this.unfilteredProducts.splice(card.productIdx, 0, card);
      }

      // Apply CouponAggregationService output (banner overwriteCoupon, email capture,
      // URL params, per-card craft xgritCoupon) to every checkout URL this card
      // will hand to its child component — override (card.ctaLink), API-generated
      // checkoutLink, and the optional learnMoreLink. Runs for cards with or without
      // a productEntry so global coupons apply consistently.
      this.applyAggregatedCouponsToCheckoutUrl(card, banner.active, environmentCoupons);

      const hasWebinarCourseIds =
        card.xGritProduct?.craftData?.webinarCourseIds &&
        card.xGritProduct?.craftData?.webinarCourseIds.length > 0 &&
        card.xGritProduct?.craftData.webinarCourseIds.filter(
          (courseId) =>
            courseId !== null && courseId !== undefined && courseId.courseid && courseId.courseid.length > 0,
        ).length > 0;
      // If webinarSchedule is present on the product entry, we need to get the webinar data
      if (hasWebinarCourseIds) {
        const webinarTitle = card.xGritProduct?.craftData?.webinarTitle;
        const webinarContent = card.xGritProduct?.craftData?.webinarContent;
        // Pass the entire product entry to let the service determine the appropriate category automatically
        this.xas
          .getWebinarSchedule(
            card.xGritProduct.craftData.webinarCourseIds,
            card.xGritProduct.craftData,
            webinarTitle,
            webinarContent,
          )
          .subscribe({
            next: (webinarData: WebinarScheduleResponse[]) => {
              card.webinarData = webinarData;
              if (card.webinarData && card.webinarData.length > 0) {
                this.webinarDataCount++;
              }
            },
            error: (error: { status?: number }) => {
              // Webinar schedule API failed - component continues without webinar data
              // Only log non-400 errors to avoid console spam from expected validation failures
              if (error?.status !== 400) {
                // eslint-disable-next-line no-console
                console.warn('Failed to load webinar schedule for product card:', error);
              }
              card.webinarData = [];
            },
          });
      }
    });

    // Force Angular change detection to ensure template updates with new pricing
    this.cdr.detectChanges();
    this.schemaService.register(getProductCardSchema(this.products, this.document.URL));
  }

  /**
   * Enhances the card's checkout URL with all coupons from CouponAggregationService
   * (banner overwriteCoupon, email capture, URL params, plus the per-card craft
   * xgritCoupon). Mutates whichever URL the child component will actually render:
   * `card.ctaLink` for an override, otherwise `card.xGritProduct.apiData.checkoutLink`.
   * The shared CheckoutUrlBuilderService is idempotent — VisionButton (default cards)
   * re-running it on the same URL dedupes via Set.
   *
   * Only absolute http(s) checkout URLs are handed to the builder; the underlying
   * URL constructor throws on relative paths.
   */
  private applyAggregatedCouponsToCheckoutUrl(
    card: ProductItem,
    bannerCoupons: string[],
    environmentCoupons: string[],
  ): void {
    const craftProducts =
      card.productEntry?.length && this.ps.isProductValid(card.productEntry)
        ? [this.ps.extractProduct(card.productEntry)]
        : undefined;

    // Normalize the Craft link hosts first: a non-checkout handoff (AARP's course-selection) skips the builder below.
    if (card.ctaLink) card.ctaLink = this.checkoutUrlBuilderService.toEnvCheckoutHost(card.ctaLink);
    if (card.learnMoreLink) card.learnMoreLink = this.checkoutUrlBuilderService.toEnvCheckoutHost(card.learnMoreLink);

    // The CTA path: override (`card.ctaLink`) wins over API (`apiData.checkoutLink`).
    // The child's resolution is `ctaLink ?? apiData.checkoutLink` — it never uses the
    // API URL when an override is set, even if that override isn't a checkout URL.
    // Mirror that here: enhance the override when it's a checkout URL; otherwise
    // enhance the API URL only when there's no override at all.
    if (this.isEnhanceableCheckoutUrl(card.ctaLink)) {
      card.ctaLink = this.enhanceCheckoutUrl(card.ctaLink, craftProducts, bannerCoupons, environmentCoupons);
    } else if (!card.ctaLink) {
      const apiCheckoutLink = card.xGritProduct?.apiData?.checkoutLink;
      if (this.isEnhanceableCheckoutUrl(apiCheckoutLink)) {
        card.xGritProduct.apiData.checkoutLink = this.enhanceCheckoutUrl(
          apiCheckoutLink,
          craftProducts,
          bannerCoupons,
          environmentCoupons,
        );
      }
    }

    // `learnMoreLink` is independent of the CTA — Craft authors can point it at a
    // checkout URL too. Previously `CardItemComponent.couponParameterCheck()` mutated
    // it to add the per-product coupon; with that removed, enhance it here so
    // banner/email/URL/craft coupons all apply consistently.
    if (this.isEnhanceableCheckoutUrl(card.learnMoreLink)) {
      card.learnMoreLink = this.enhanceCheckoutUrl(
        card.learnMoreLink,
        craftProducts,
        bannerCoupons,
        environmentCoupons,
      );
    }
  }

  private enhanceCheckoutUrl(
    url: string,
    craftProducts: CraftXGritProductEntry[] | undefined,
    bannerCoupons: string[],
    environmentCoupons: string[],
  ): string {
    return this.checkoutUrlBuilderService.buildCheckoutUrlWithAllCoupons(
      url,
      craftProducts,
      bannerCoupons,
      environmentCoupons,
    );
  }

  /**
   * Walks this component's scoped ActivatedRoute down to find the route node that
   * owns the resolver's pageData, then returns the banner overwriteCoupon — but
   * only when the banner is currently within its active sale window. An empty
   * array means "no active overwrite", which signals merge-mode to the builder;
   * a non-empty array signals replace-mode (overwrites every other coupon on the
   * checkout URL).
   */
  private collectBannerCouponsFromScopedRoute(): { active: string[]; all: string[] } {
    const empty = { active: [], all: [] };
    try {
      const findPageData = (route: ActivatedRoute | null | undefined): unknown => {
        let current = route;
        while (current) {
          const data: unknown = current.snapshot?.data?.pageData;
          if (data) return data;
          current = current.firstChild;
        }
        return null;
      };

      const pageData = findPageData(this.route) as
        | {
            craftData?: {
              banners?: { topBanner?: BannerWithOverwriteCoupon[] };
              header?: {
                aceHeaderPromoBanner?: BannerWithOverwriteCoupon[];
                realEstateHeaderPromoBanner?: BannerWithOverwriteCoupon[];
              };
            };
          }
        | null
        | undefined;

      if (!pageData) {
        return empty;
      }

      const banners: BannerWithOverwriteCoupon[] = [
        pageData?.craftData?.banners?.topBanner?.[0],
        pageData?.craftData?.header?.aceHeaderPromoBanner?.[0],
        pageData?.craftData?.header?.realEstateHeaderPromoBanner?.[0],
      ];

      const all: string[] = [];
      let active: string[] = [];

      // Walk every banner shape. `all` accumulates the coupon strings for every
      // banner regardless of active/visibility state — callers use it to filter
      // banner values out of CouponAggregationService's environment-coupon list,
      // so the active gate below is the sole authority on whether the banner
      // actually applies to the checkout URL. `active` returns the FIRST banner
      // shape that passes both the visibility toggle and the date-window check.
      for (const banner of banners) {
        const raw = typeof banner?.overwriteCoupon === 'string' ? banner.overwriteCoupon.trim() : '';
        if (!raw) continue;

        const coupons = raw
          .split(',')
          .map((c: string) => c.trim())
          .filter((c: string) => Boolean(c));

        all.push(...coupons);

        if (active.length === 0 && this.isBannerActive(banner)) {
          active = coupons;
        }
      }

      return { active, all };
    } catch (error: unknown) {
      // Route resolver data is untyped — defensively swallow any structural
      // surprises rather than letting a malformed banner shape kill parseProducts.
      // Normalize to a real Error so logError can safely read message/stack even
      // if a non-Error value was thrown.
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logError({
        error: normalizedError,
        message: 'PRODUCT_CARD_BANNER_COUPON_READ_ERROR',
        tags: ['COUPON', 'BANNER'],
      });
      return empty;
    }
  }

  /**
   * A banner is "active" — and therefore its `overwriteCoupon` should replace
   * existing checkout coupons — only when BOTH of the following are true:
   *
   *  1. `showHidePromoBanner` is truthy. Craft authors use this toggle to actually
   *     show/hide the banner on the site. If the banner isn't visible to the user,
   *     its coupon must not silently apply at checkout.
   *  2. NOW falls inside the promotion window (mirrors QueryParamDirective.isOfferTimeRange):
   *     between `promotionStartDateTime` and `promotionEndsDateTime`, with optional
   *     `promotionReset[0].promotionEndsDateTime` override. When neither date is set,
   *     the window is treated as always-open. A partial range — only start, or only
   *     end — is treated as inactive.
   */
  private isBannerActive(banner: BannerWithOverwriteCoupon | undefined): boolean {
    if (!banner) return false;

    // Visibility toggle takes precedence — a hidden banner is never active.
    if (banner.showHidePromoBanner !== true) return false;

    const hasStart = !!banner.promotionStartDateTime;
    const hasEnd = !!banner.promotionEndsDateTime;
    if (!hasStart && !hasEnd) return true;
    if (!hasStart || !hasEnd) return false;

    const now = Date.now();
    const start = new Date(banner.promotionStartDateTime).valueOf();
    const resetEnd = banner.promotionReset?.[0]?.promotionEndsDateTime;
    const end = resetEnd ? new Date(resetEnd).valueOf() : new Date(banner.promotionEndsDateTime).valueOf();

    return start <= now && now <= end;
  }

  private isEnhanceableCheckoutUrl(url: string | undefined | null): url is string {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.startsWith('http') && lower.includes('checkout');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
